import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  NgZone,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import type { TSimpleChanges } from '@ppl/utils/angular.utils';
import { notFirstChange } from '@ppl/utils/angular.utils';
import { FormValueControl } from '@ppl/utils/form.utils';
import { roundToDecimal } from '@ppl/utils/math.utils';
import { sanitizeNumberValue } from '@ppl/utils/number-sanitization.utils';
import type InputMask from 'imask/esm/controls/input';
import IMask from 'imask/esm/imask';
import 'imask/esm/masked/number';
import type { MaskedNumberOptions } from 'imask/esm/masked/number';
import { PplInputIntl } from '../input-intl';

@Component({
  selector: 'ppl-input-number',
  templateUrl: './input-number.component.html',
  styleUrls: ['./input-number.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PplInputNumberComponent),
      multi: true
    }
  ],
  standalone: false
})
@FormValueControl()
export class PplInputNumberComponent implements AfterViewInit, OnDestroy {
  @Input() value: number;
  @Input() placeholder = '';
  @Input() min: number;
  @Input() max: number;
  @Input() decimal = true;
  @Input() allowLeadingZeroes = false;
  @Input() allowNull = false;
  @Input() allowNegative = true;
  @Input() roundTo = 4;
  @Input() usesThousandSeparator = true;
  @Input() height: string | false;
  @Input() prependIcon: string;
  @Input() prependText: string;
  @Input() clear = false;
  @Input() clearIcon?: string;
  @Input() autoFill = false;
  @Input() autoFocus = false;
  // masks input based on inputIntl. Ignored in case of 'default'
  @Input() mask: InputNumberMask = 'default';

  @Input() @HostBinding('class.controls') controls = true;
  @Input() @HostBinding('class.has-unit') unit: string;
  @Input() @HostBinding('class.disabled') disabled = false;
  @Input() @HostBinding('class.readonly') readonly = false;
  @Input() @HostBinding('class.left') alignLeft = true;

  @Output() valueChange = new EventEmitter<number>();
  @Output() clearClick = new EventEmitter();
  @Output() enter = new EventEmitter();
  @Output() focus = new EventEmitter();
  @Output() blur = new EventEmitter();

  @ViewChild('valueInput', { static: true }) valueInput: ElementRef;

  inputMask: InputMask<any>;

  Math = Math;

  @HostBinding('class.has-prepend-icon')
  get hasIcon() {
    return !!this.prependIcon;
  }

  get maskOptions() {
    return getMaskOptions(this.inputIntl, this.mask, this.roundTo, this.decimal, this.usesThousandSeparator);
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private inputIntl: PplInputIntl,
    private ngZone: NgZone
  ) { }

  ngOnChanges(changes: TSimpleChanges<PplInputNumberComponent>) {
    if (changes.value) {
      if (this.inputMask?.typedValue as number !== this.value) {
        this.setInputValue();
      }
    }

    if (notFirstChange(changes.roundTo)) {
      this.inputMask.updateOptions({
        scale: this.maskOptions.scale
      });
    }
  }

  ngOnInit() {
    if (this.value !== undefined) {
      this.setInputValue();
    }

    if (this.autoFocus) {
      this.valueInput.nativeElement?.focus();
    }
  }

  ngAfterViewInit(): void {
    const formatOptions = this.maskOptions;

    const min = this.allowNegative
      ? this.min
      : 0;
    const max = this.max;

    // configuration: https://imask.js.org/guide.html#masked-number
    this.inputMask = IMask(this.valueInput.nativeElement, {
      mask: Number,  // enable number mask
      scale: formatOptions.scale,
      thousandsSeparator: formatOptions.thousandsSeparator,  // any single char
      radix: formatOptions.radix,  // fractional delimiter
      mapToRadix: ['.', ','], // symbols to process as radix
      ...(min !== undefined && { min }),
      ...(max !== undefined && { max }),
    } satisfies Partial<MaskedNumberOptions>);

    this.setInputValue();

    this.inputMask.on('accept', () => {
      let value = this.inputMask.typedValue as number;

      if (this.inputMask.value === '') {
        if (this.allowNull) {
          value = null;
        } else {
          value = 0;
        }
      }

      if (this.roundToDecimal(sanitizeNumberValue(this.value, true)) !== value) {
        this.ngZone.run(() => {
          this.valueChange.emit(value);
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.inputMask?.destroy();
  }

  onBumpValue(diff: number) {
    if (!this.disabled && !this.readonly) {
      const value = sanitizeNumberValue(this.value);
      let newValue = value + diff;

      this.safelyEmitValue(newValue);
    }
  }

  onKeyDown($event: KeyboardEvent) {
    const altKey = $event.altKey;
    const multiple = altKey ? 0.1 : 1;

    switch ($event.key) {
      case 'Enter':
        this.enter.emit();
        return false;
      case 'Up':
      case 'ArrowUp':
        this.onBumpValue(1 * multiple);
        $event.preventDefault();
        return false;
      case 'Down':
      case 'ArrowDown':
        this.onBumpValue(-1 * multiple);
        $event.preventDefault();
        return false;
    }
  }

  onPaste($event: ClipboardEvent) {
    $event.preventDefault();

    let paste = $event?.clipboardData?.getData("text");
    if (!this.disabled && !this.readonly && typeof paste === 'string') {
      // make sure we remove thousand/group separator for easier paste
      paste = paste.split(this.inputIntl.group).join('');
      // this setter should hopefully recognize all probable/improbable user inputs
      this.inputMask.value = paste;
    }
  }

  setFocus() {
    this.valueInput.nativeElement?.focus();
  }

  private setInputValue() {
    if (this.inputMask) {
      if (this.value === null || this.value === undefined) {
        this.inputMask.value = '';
      } else {
        this.inputMask.typedValue = this.value;
      }
    }
  }

  private roundToDecimal(value: number | null) {
    return value === null || value === undefined
      ? null
      : roundToDecimal(this.value, this.roundTo);
  }

  private safelyEmitValue(newValue: number) {
    if (typeof newValue !== 'number') {
      return;
    }

    if (this.decimal) {
      newValue = +newValue.toFixed(this.roundTo);
    }
    if ((this.min === undefined || this.min <= newValue) && (this.max === undefined || newValue <= this.max)) {
      this.valueChange.emit(newValue);
    }
  }
}

export type InputNumberMask = 'default' | 'currency' | 'decimal' | 'integer';

function getMaskOptions(
  inputIntl: PplInputIntl,
  format: InputNumberMask,
  roundTo: number,
  decimal: boolean,
  usesThousandSeparator: boolean
): {
  thousandsSeparator: string;
  radix: string;
  scale: number;
} {
  switch (format) {
    case 'default':
      // no special format options except for radix(decimal char) & scale is forwarded
      return {
        thousandsSeparator: '',
        radix: inputIntl.decimal,
        // digits after point, 0 for integers
        scale: decimal ? roundTo : 0
      };
    case 'decimal':
    case 'currency':
      return {
        thousandsSeparator: usesThousandSeparator ? inputIntl.group : '',
        radix: inputIntl.decimal,
        // digits after point, 0 for integers
        scale: decimal ? roundTo : 0
      };
    case 'integer':
      return {
        thousandsSeparator: usesThousandSeparator ? inputIntl.group : '',
        radix: undefined,
        scale: 0
      };
  }
}
